import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { neutral } from '../../styles/mui/colors';
import { getConsent, setConsent } from '../../util/consent/consentState';
import { newPrompt, sendConsentTelemetry } from '../../util/consent/consentTelemetry';
import { OPEN_COOKIE_SETTINGS_EVENT } from '../../util/consent/consentUI';
import useAppTheme from '../../util/hooks/useAppTheme';
import useScreenSize from '../../util/hooks/useScreenSize';
import { logError } from '../../util/log';
import { requestTrackingPermission } from '../../util/mobile/appTracking';
import { isCordova, isIOS } from '../../util/mobile/deviceDetection';
import Link from '../Links/Link/Link';
import CookieSettingsDialog from './CookieSettingsDialog';
import { startupScreenSx } from './startupScreenSx';

const bannerBaseSx = {
  bgcolor: 'background.paper',
  border: `2px solid ${neutral[100]}`,
  boxShadow: 3,
  p: 2,
  position: 'fixed',
};

// 560px so all three action buttons fit on one row (longest locales wrap otherwise).
const desktopSx = { ...bannerBaseSx, borderRadius: 2, bottom: 16, maxWidth: 560, right: 16 };
// The bottom bar paints above the banner (zIndex drawer+1 vs. 200), and its raised
// centre action ("Entdecken") sticks ~24px above the bar — a flush `BottomBar_height`
// still clips the last button, so pad past both.
const mobileSx = {
  ...bannerBaseSx,
  bottom: 0,
  left: 0,
  pb: 'calc(var(--BottomBar_height) + 40px)',
  right: 0,
};
// Native app: not a banner but a full-screen startup consent screen — the first
// screen on first launch, in the shared startup-screen look.
// Decline stays a first-class, equally prominent choice (GDPR Art. 7(4) —
// consent must be refusable; Apple 3.2.2 forbids gating app functionality on
// tracking consent).
const appScreenSx = startupScreenSx;

// The "shown" impression telemetry must fire once per undecided session, not
// once per mounted <CookieConsent/> — Page remounts it on every SPA navigation,
// which used to emit a fresh promptId + "shown" POST per page view. sessionStorage
// survives those remounts (and full reloads within the tab) and clears itself
// once the tab closes; a decision hides the banner permanently anyway.
const SHOWN_FLAG_KEY = 'vansite.consentShownSent';
const shownAlreadySent = () => {
  try {
    return window.sessionStorage.getItem(SHOWN_FLAG_KEY) === '1';
  } catch (e) {
    return false;
  }
};
const markShownSent = () => {
  try {
    window.sessionStorage.setItem(SHOWN_FLAG_KEY, '1');
  } catch (e) {
    // sessionStorage unavailable (locked-down webview) — worst case a duplicate
    // "shown" per navigation, which is the pre-existing behaviour, not a crash.
  }
};

const useCookieConsent = () => {
  const intl = useIntl();
  const [showBanner, setShowBanner] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (getConsent()) return;
    // Undecided: (re)show the banner — showBanner is local state that resets on
    // remount, so this must run on every mount.
    setShowBanner(true);
    // Impression telemetry once per undecided session. Minting the promptId here
    // links the eventual decision back to this impression.
    if (!shownAlreadySent()) {
      markShownSent();
      newPrompt();
      sendConsentTelemetry('shown', {}, intl.locale);
    }
  }, [intl.locale]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const open = () => setDialogOpen(true);
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, open);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, open);
  }, []);

  const decide = useCallback(
    async ({ statistics, marketing }, action) => {
      // The first decision (startup screen) leaves ATT to the onboarding in-app message,
      // which explains it first. Enabling marketing later in the settings asks here, so ATT
      // stays reachable for users who never saw that message. Asked before vendors boot;
      // the effective gate (hasEffectiveConsent) re-checks the ATT status on every event.
      if (marketing && !showBanner && isCordova() && isIOS()) {
        // A failing plugin must not swallow the consent decision itself.
        await requestTrackingPermission().catch((e) => logError(e, 'att-request-failed'));
      }
      setConsent({ statistics, marketing });
      sendConsentTelemetry(action, { statistics, marketing }, intl.locale);
      setShowBanner(false);
      setDialogOpen(false);
    },
    [intl.locale, showBanner],
  );

  return { decide, dialogOpen, setDialogOpen, showBanner };
};

const CookieConsentComponent = () => {
  const { isMobile, isTablet } = useScreenSize();
  const theme = useAppTheme();
  const { decide, dialogOpen, setDialogOpen, showBanner } = useCookieConsent();

  if (typeof window === 'undefined') return null;

  const inApp = isCordova();
  const bannerSx = inApp ? appScreenSx : isMobile || isTablet ? mobileSx : desktopSx;
  const stackButtons = inApp || isMobile;

  return (
    <>
      {showBanner && !dialogOpen ? (
        // In the app the startup consent screen must block the whole shell —
        // TopBar sits at z 10 but the BottomNavigationBar at theme.zIndex.drawer
        // (1200), so the web banner z (200) left the bars tappable underneath.
        <Box
          sx={{
            ...bannerSx,
            zIndex: inApp ? theme.zIndex.modal : theme.globals.zIndexCookieBanner,
          }}
        >
          <Typography fontWeight="bold" mb={1} variant={inApp ? 'h5' : 'h6'}>
            <FormattedMessage id="CookieConsentV2.title" />
          </Typography>
          <Typography mb={2} variant="body2">
            {/* "Website" is wrong inside the native app shell. */}
            <FormattedMessage
              id={inApp ? 'CookieConsentV2.messageApp' : 'CookieConsentV2.message'}
              values={{
                privacyLink: (
                  <Link to={{ name: 'StoryblokPage', params: { slug: 'privacy-policy' } }}>
                    <FormattedMessage id="CookieConsentV2.privacyLink" />
                  </Link>
                ),
              }}
            />
          </Typography>
          {/* Order (left → right / top → bottom): Einstellungen, Nur notwendige,
              Alle akzeptieren — accept-all sits last. */}
          <Box
            alignItems="center"
            display="flex"
            flexDirection={inApp ? 'column' : 'row'}
            flexWrap="wrap"
            gap={2}
          >
            <Button
              color="secondary"
              fullWidth={stackButtons}
              onClick={() => setDialogOpen(true)}
              variant="text"
            >
              <FormattedMessage id="CookieConsentV2.settings" />
            </Button>
            {/* One-click refusal must be as easy as one-click accept (GDPR/ePrivacy
                symmetry) and persist the decision, not just re-open on next page. */}
            <Button
              color="secondary"
              fullWidth={stackButtons}
              onClick={() => decide({ statistics: false, marketing: false }, 'necessary_only')}
              variant="outlined"
            >
              <FormattedMessage id="CookieConsentV2.necessaryOnly" />
            </Button>
            <Button
              color="secondary"
              fullWidth={stackButtons}
              onClick={() => decide({ statistics: true, marketing: true }, 'accept_all')}
              variant="contained"
            >
              <FormattedMessage id="CookieConsentV2.acceptAll" />
            </Button>
          </Box>
        </Box>
      ) : null}
      <CookieSettingsDialog isOpen={dialogOpen} onClose={() => setDialogOpen(false)} onDecide={decide} />
    </>
  );
};

export default memo(CookieConsentComponent);
